/**
 * «القياسات» — one timeline of measurements, and its curve (المرحلة 1.2, P07).
 *
 * Whoever took a reading — the patient at home, a nurse on the ward, a doctor
 * in the visit, a device — it is one row on one timeline (models/Observation.js),
 * and the curve reads that. Each point says where it came from.
 *
 *   PatientMeasurements   the patient's own «ملفي الطبي» tab: the curve per
 *                         kind, and a reading of their own when the platform's
 *                         `selfObservations` policy lets them; under «needs
 *                         approval» their readings join the curve once a desk
 *                         approves them
 *   FileMeasurements      the same curve on an institution's file, with the
 *                         readings the linked patient entered, to approve
 *
 * The curve is plain SVG drawn here, no chart library; time runs with the
 * reading direction (right to left in Arabic).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, EmptyState, Icon, Input, Panel, Spinner } from '../../components/common';
import { observationApi, patientHealthApi } from '../../api/endpoints';
import { useNotifications } from '../../context/NotificationContext';
import { useI18n } from '../../i18n/I18nProvider';
import { formatDate, formatDateTime } from '../../domain/locale';

/** What the screens chart: blood pressure as one curve of two lines. */
export const CHARTS = Object.freeze([
  { id: 'bp', kinds: ['bp_sys', 'bp_dia'], unit: 'mmHg' },
  { id: 'glucose', kinds: ['glucose'], unit: 'mg/dL' },
  { id: 'weight', kinds: ['weight'], unit: 'kg' },
  { id: 'hr', kinds: ['hr'], unit: 'bpm' },
  { id: 'temp', kinds: ['temp'], unit: '°C' },
  { id: 'spo2', kinds: ['spo2'], unit: '%' },
]);

const SOURCE_ICON = { patient: 'user-round', nurse: 'heart-pulse', doctor: 'stethoscope', device: 'activity' };

/**
 * A line per kind over time.
 *
 * @param {Array<{kind, value, at, source, approved}>} points
 * @param {string[]} kinds   the kinds drawn, in order (the first is the main line)
 */
export function MeasurementCurve({ points, kinds, unit, height = 220 }) {
  const { t, isRtl, lang } = useI18n();
  // Drawn at its real width, so its labels read at their real size on a phone and a desktop alike.
  const box = useRef(null);
  const [W, setW] = useState(640);
  useEffect(() => {
    const el = box.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const watch = new ResizeObserver(([entry]) => setW(Math.max(240, Math.round(entry.contentRect.width))));
    watch.observe(el);
    return () => watch.disconnect();
  }, []);
  const H = height;
  const pad = { x: 44, top: 18, bottom: 34 };
  const series = kinds.map((kind) => points.filter((p) => p.kind === kind).sort((a, b) => new Date(a.at) - new Date(b.at)));
  const all = series.flat();
  if (!all.length) return null;

  const times = all.map((p) => new Date(p.at).getTime());
  const values = all.map((p) => p.value);
  const t0 = Math.min(...times);
  const t1 = Math.max(...times);
  const spread = Math.max(1, (Math.max(...values) - Math.min(...values)) * 0.15);
  const v0 = Math.min(...values) - spread;
  const v1 = Math.max(...values) + spread;
  const x = (time) => {
    const f = t1 === t0 ? 0.5 : (time - t0) / (t1 - t0);
    const pos = pad.x + f * (W - pad.x * 2);
    return isRtl ? W - pos : pos;
  };
  const y = (v) => pad.top + (1 - (v - v0) / (v1 - v0)) * (H - pad.top - pad.bottom);
  const ticks = [v0 + (v1 - v0) * 0.15, (v0 + v1) / 2, v1 - (v1 - v0) * 0.15];
  const fmt = (v) => (Math.abs(v) >= 100 ? Math.round(v) : Math.round(v * 10) / 10);

  return (
    <figure className="curve" data-testid="measurement-curve" ref={box}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={t('measurements.curveLabel', { unit })}>
        {ticks.map((v) => (
          <g key={v}>
            <line x1={pad.x} x2={W - pad.x} y1={y(v)} y2={y(v)} className="curve__grid" />
            <text x={isRtl ? W - pad.x + 6 : pad.x - 6} y={y(v) + 4} className="curve__tick" textAnchor={isRtl ? 'start' : 'end'}>
              {fmt(v)}
            </text>
          </g>
        ))}
        {series.map((line, i) =>
          line.length ? (
            <g key={kinds[i]} className={`curve__series curve__series--${i}`}>
              <polyline points={line.map((p) => `${x(new Date(p.at).getTime())},${y(p.value)}`).join(' ')} className="curve__line" />
              {line.map((p) => (
                <circle key={p.id} cx={x(new Date(p.at).getTime())} cy={y(p.value)} r={4.5} className={`curve__dot curve__dot--${p.source}`}>
                  <title>{`${fmt(p.value)} ${unit} · ${formatDateTime(p.at, { lang })} · ${t(`measurements.source.${p.source}`)}`}</title>
                </circle>
              ))}
            </g>
          ) : null,
        )}
        <text x={x(t0)} y={H - 10} className="curve__axis" textAnchor="middle">
          {formatDate(new Date(t0), { lang })}
        </text>
        {t1 !== t0 && (
          <text x={x(t1)} y={H - 10} className="curve__axis" textAnchor="middle">
            {formatDate(new Date(t1), { lang })}
          </text>
        )}
      </svg>
      {kinds.length > 1 && (
        <figcaption className="curve__legend">
          {kinds.map((kind, i) => (
            <span key={kind} className={`curve__key curve__key--${i}`}>
              {t(`measurements.kind.${kind}`)}
            </span>
          ))}
        </figcaption>
      )}
    </figure>
  );
}

/** The latest readings of a chart, newest first, each with where it came from. */
function ReadingList({ points, kinds, unit, onApprove }) {
  const { t, lang } = useI18n();
  const rows = points.filter((p) => kinds.includes(p.kind)).sort((a, b) => new Date(b.at) - new Date(a.at)).slice(0, 8);
  // A blood pressure is two rows taken together: shown as one «120/80».
  const shown = [];
  for (const p of rows) {
    if (p.kind === 'bp_dia') continue;
    const dia = p.kind === 'bp_sys' ? rows.find((d) => d.kind === 'bp_dia' && Math.abs(new Date(d.at) - new Date(p.at)) < 60000) : null;
    shown.push({ ...p, text: dia ? `${p.value}/${dia.value}` : String(p.value), pair: dia });
  }
  return (
    <ul className="readings">
      {shown.map((p) => (
        <li key={p.id} className="readings__row">
          <strong dir="ltr">
            {p.text} <small>{unit}</small>
          </strong>
          <span className="readings__when">{formatDateTime(p.at, { lang })}</span>
          <Badge icon={SOURCE_ICON[p.source] || 'activity'}>{t(`measurements.source.${p.source}`)}</Badge>
          {!p.approved && <span className="readings__pending">{t('measurements.pending')}</span>}
          {!p.approved && onApprove && (
            <Button variant="ghost" icon="check" onClick={() => onApprove([p, p.pair].filter(Boolean))} data-testid={`approve-${p.id}`}>
              {t('measurements.approve')}
            </Button>
          )}
        </li>
      ))}
    </ul>
  );
}

function ChartPicker({ charts, active, onPick, counts }) {
  const { t } = useI18n();
  return (
    <div className="hp-choice" role="tablist">
      {charts.map((c) => (
        <button key={c.id} type="button" role="tab" aria-selected={active === c.id} className={`hp-chip ${active === c.id ? 'is-on' : ''}`} onClick={() => onPick(c.id)} data-testid={`chart-${c.id}`}>
          {t(`measurements.chart.${c.id}`)}
          {counts[c.id] ? <span className="hp-chip__count">{counts[c.id]}</span> : null}
        </button>
      ))}
    </div>
  );
}

const countsOf = (points) => Object.fromEntries(CHARTS.map((c) => [c.id, points.filter((p) => p.kind === c.kinds[0]).length]));

/** The patient's own tab. */
export function PatientMeasurements() {
  const { t } = useI18n();
  const { success, error: toastError } = useNotifications();
  const [data, setData] = useState(null);
  const [chart, setChart] = useState(null);
  const [form, setForm] = useState({ value: '', dia: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    observationApi
      .list()
      .then((answer) => setData(answer))
      .catch((err) => setData({ observations: [], error: err }));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const points = useMemo(() => (data ? data.observations : []), [data]);
  const counts = useMemo(() => countsOf(points), [points]);
  const active = chart || (CHARTS.find((c) => counts[c.id]) || CHARTS[0]).id;
  const spec = CHARTS.find((c) => c.id === active);

  if (!data) return <Spinner variant="inline" label={t('measurements.loading')} />;

  const add = async () => {
    setBusy(true);
    try {
      const at = new Date().toISOString();
      let waits = false;
      if (spec.id === 'bp') {
        waits = (await observationApi.add({ kind: 'bp_sys', value: form.value, at })).waitsForApproval;
        await observationApi.add({ kind: 'bp_dia', value: form.dia, at });
      } else {
        waits = (await observationApi.add({ kind: spec.kinds[0], value: form.value, at })).waitsForApproval;
      }
      success(waits ? t('measurements.addedWaits') : t('measurements.added'));
      setForm({ value: '', dia: '' });
      load();
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const policy = data.policy || { entry: 'yes', approval: 'none' };
  return (
    <Panel className="measurements" data-testid="patient-measurements">
      <ChartPicker charts={CHARTS} active={active} onPick={setChart} counts={counts} />
      {counts[active] ? (
        <>
          <MeasurementCurve points={points} kinds={spec.kinds} unit={spec.unit} />
          <ReadingList points={points} kinds={spec.kinds} unit={spec.unit} />
        </>
      ) : (
        <EmptyState icon="activity" title={t('measurements.emptyTitle')} message={t('measurements.emptyMessage')} />
      )}
      {data.pending > 0 && (
        <p className="hp-note">
          <Icon name="clock" size={14} /> {t('measurements.pendingCount', { n: data.pending })}
        </p>
      )}
      {policy.entry !== 'no' && (
        <div className="measurements__add">
          <h4>{t('measurements.addTitle', { chart: t(`measurements.chart.${active}`) })}</h4>
          <div className="hp-inline">
            <Input type="number" inputMode="decimal" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder={spec.id === 'bp' ? t('measurements.kind.bp_sys') : spec.unit} data-testid="reading-value" />
            {spec.id === 'bp' && <Input type="number" inputMode="numeric" value={form.dia} onChange={(e) => setForm({ ...form, dia: e.target.value })} placeholder={t('measurements.kind.bp_dia')} data-testid="reading-dia" />}
            <Button icon="plus" onClick={add} loading={busy} disabled={!form.value || (spec.id === 'bp' && !form.dia)} data-testid="reading-add">
              {t('measurements.add')}
            </Button>
          </div>
          {policy.approval === 'required' && <p className="hp-note">{t('measurements.approvalNote')}</p>}
        </div>
      )}
    </Panel>
  );
}

/** An institution's file: the curve from every source, and the patient's readings to approve. */
export function FileMeasurements({ fileId, canApprove = false }) {
  const { t } = useI18n();
  const { success, error: toastError } = useNotifications();
  const [data, setData] = useState(null);
  const [chart, setChart] = useState(null);

  const load = useCallback(() => {
    patientHealthApi
      .observations(fileId)
      .then(setData)
      .catch(() => setData({ observations: [] }));
  }, [fileId]);
  useEffect(() => {
    load();
  }, [load]);

  const points = useMemo(() => (data ? data.observations : []), [data]);
  const counts = useMemo(() => countsOf(points), [points]);
  if (!data || !points.length) return null;
  const active = chart || (CHARTS.find((c) => counts[c.id]) || CHARTS[0]).id;
  const spec = CHARTS.find((c) => c.id === active);
  const approve = async (rows) => {
    try {
      for (const row of rows) await patientHealthApi.approveObservation(fileId, row.id);
      success(t('measurements.approved'));
      load();
    } catch (err) {
      toastError(err.message);
    }
  };

  return (
    <Panel className="measurements" data-testid="file-measurements">
      <h3 className="measurements__title">
        <Icon name="activity" size={18} /> {t('measurements.title')}
      </h3>
      <ChartPicker charts={CHARTS.filter((c) => counts[c.id])} active={active} onPick={setChart} counts={counts} />
      <MeasurementCurve points={points} kinds={spec.kinds} unit={spec.unit} height={180} />
      <ReadingList points={points} kinds={spec.kinds} unit={spec.unit} onApprove={canApprove ? approve : null} />
    </Panel>
  );
}

export default PatientMeasurements;
